import { defineStore } from 'pinia'
import client from '@/api/client'
import { storageKey } from '@/config/host'

export const useAuthStore = defineStore('auth', {
  // Session keys are namespaced per tenant (storageKey) so two schools open on
  // the same shared origin never share a login.
  state: () => ({
    token: localStorage.getItem(storageKey('token')) || null,
    user: JSON.parse(localStorage.getItem(storageKey('user')) || 'null'),
    permissions: JSON.parse(localStorage.getItem(storageKey('permissions')) || '[]'),
    plan: JSON.parse(localStorage.getItem(storageKey('plan')) || 'null'),
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    role: (state) => state.user?.role || null,
    // Permission check for UI gating. super_admin always passes.
    can: (state) => (code) =>
      state.user?.role === 'super_admin' || state.permissions.includes(code),
    // Plan feature gate for UI. Mirrors backend Plan::hasFeature; if the plan
    // hasn't loaded yet, allow (backend is authoritative and will 403 anyway).
    hasFeature: (state) => (key) => {
      const feats = state.plan?.features
      if (!feats) return true
      return feats.includes('*') || feats.includes(key)
    },
    planTier: (state) => state.plan?.tier || null,
    // Live subscription lock reason derived from the plan's own dates/status,
    // mirroring the backend lifecycle gate in index.php. Returns 'suspended' |
    // 'trial_ended' | 'expired', or null when the plan is active (or free — free
    // never locks). Single source of truth for the lockout UI.
    subscriptionLockReason: (state) => {
      const plan = state.plan
      if (!plan) return null
      if (['suspended', 'cancelled'].includes(plan.status)) return 'suspended'
      const graceMs = (Number(plan.graceDays) || 0) * 86400000
      const past = (raw) => {
        if (!raw) return false
        const t = new Date(String(raw).replace(' ', 'T')).getTime()
        return !isNaN(t) && t + graceMs < Date.now()
      }
      if (['trial', 'trialing'].includes(plan.status)) {
        return past(plan.trialEndsAt) ? 'trial_ended' : null
      }
      if ((plan.tier || 'free') === 'free') return null
      return past(plan.expiresAt) ? 'expired' : null
    },
    homeRoute: (state) => {
      const role = state.user?.role
      if (role === 'student') return '/portal/admin/dashboard'
      if (role === 'parent') return '/portal/admin/dashboard'
      if (role === 'teacher') return '/portal/teacher/classes'
      if (role === 'principal') return '/portal/admin/results'
      if (role === 'librarian') return '/portal/admin/library/books'
      return '/portal/admin/dashboard'
    },
  },

  actions: {
    async login(credentials) {
      const { data } = await client.post('/auth/login', credentials)
      this.setSession(data.token, data.user)
      await Promise.all([this.loadPermissions(), this.loadPlan()])
      return data.user
    },

    // Fetch the school's plan snapshot (tier/caps/features/usage). Best-effort.
    async loadPlan() {
      if (!this.token) return
      try {
        const { data } = await client.get('/me/plan')
        this.plan = {
          tier: data.tier,
          cycle: data.cycle,
          status: data.status,
          trialEndsAt: data.trial_ends_at || null,
          expiresAt: data.expires_at || null,
          graceDays: Number(data.grace_days) || 0,
          caps: data.caps,
          features: data.features || [],
          usage: data.usage,
          pricing: data.pricing || null,
          plans: data.plans || [],
          currency: data.currency || null,
        }
        localStorage.setItem(storageKey('plan'), JSON.stringify(this.plan))
      } catch (e) {
        // Leave any previously cached plan in place.
      }
    },

    // Fetch the caller's effective permission codes (best-effort — never blocks).
    async loadPermissions() {
      if (!this.token) return
      try {
        const { data } = await client.get('/permissions/me')
        this.permissions = data.permissions || []
        localStorage.setItem(storageKey('permissions'), JSON.stringify(this.permissions))
      } catch (e) {
        // Leave any previously cached permissions in place.
      }
    },

    async logout() {
      try {
        if (this.token) await client.post('/auth/logout')
      } catch (e) {
        // Logout is best-effort; clear locally regardless.
      }
      this.clearSession()
    },

    async forgotPassword(email) {
      const { data } = await client.post('/auth/forgot-password', { email })
      return data.message
    },

    async resetPassword(token, newPassword) {
      const { data } = await client.post('/auth/reset-password', {
        token,
        new_password: newPassword,
      })
      return data.message
    },

    async changePassword(currentPassword, newPassword) {
      const { data } = await client.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      })
      return data.message
    },

    // Enter a tenant from a platform-issued impersonation token. The token is a
    // normal tenant JWT; decode its claims to build the user object, then load
    // the plan/permissions like a real login. Used by the platform console.
    async setImpersonation(token) {
      let claims = {}
      try {
        claims = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
      } catch (e) {
        return false
      }
      this.setSession(token, {
        id: claims.sub,
        email: claims.email,
        name: claims.name,
        role: claims.role,
      })
      await Promise.all([this.loadPermissions(), this.loadPlan()])
      return true
    },

    setSession(token, user) {
      this.token = token
      this.user = user
      localStorage.setItem(storageKey('token'), token)
      localStorage.setItem(storageKey('user'), JSON.stringify(user))
    },

    clearSession() {
      this.token = null
      this.user = null
      this.permissions = []
      this.plan = null
      localStorage.removeItem(storageKey('token'))
      localStorage.removeItem(storageKey('user'))
      localStorage.removeItem(storageKey('permissions'))
      localStorage.removeItem(storageKey('plan'))
    },
  },
})
